import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, CheckCircle2, Copy, Eye, EyeOff, LifeBuoy, ListChecks, MessageSquareText, ShieldCheck, Terminal, Unplug } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
const EXAMPLES = [
  "Warum hängt Auftrag #1042 noch?",
  "Welche Artikel sind ausverkauft oder werden diese Woche knapp?",
  "Wann kommt die nächste Lieferung von Lindner und was ist schon eingebucht?",
  "Was muss ich heute im Portal erledigen?",
  "Wie viele Aufträge wurden gestern versendet, und gab es Verzögerungen?",
  "Zeig mir alle Aufträge mit der Trackingnummer 00340434…",
  "Welche Pakete haben gerade ein Zustellproblem, und wie lange sind sie nach Österreich unterwegs?",
];

type Client = "claude" | "code" | "other";

function Step({ n, title, children }: { n: number; title: string; children?: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 size-6 shrink-0 rounded-full bg-ink text-ink-fg grid place-items-center text-[12px] font-semibold tabular-nums">{n}</span>
      <div className="min-w-0 flex-1 pb-1">
        <p className="text-[13.5px] font-medium">{t(title)}</p>
        {children && <div className="mt-0.5 text-[12.5px] text-fg-muted space-y-1">{children}</div>}
      </div>
    </li>
  );
}

function CopyBox({ value, mono = true }: { value: string; mono?: boolean }) {
  const copy = () => void navigator.clipboard?.writeText(value).then(() => toast.success("Kopiert"));
  return (
    <div className="flex items-center gap-2">
      <code className={cx("flex-1 min-w-0 truncate rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-[12.5px]", !mono && "font-sans")}>{value}</code>
      <Button size="sm" icon={<Copy className="size-3.5" />} onClick={copy}>{t("Kopieren")}</Button>
    </div>
  );
}

/** KI-Assistent: Claude & Co. mit den Logiful-Daten verbinden – Erklärung, Einrichtung je Programm, Verbindungen */
export function AiAssistantPage() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [client, setClient] = useState<Client>("claude");
  const q = useQuery({ queryKey: ["mcp-connections", tenantId], queryFn: () => api.mcp.connections(tenantId) });
  const revoke = useMutation({
    mutationFn: (id: string) => api.mcp.revoke(tenantId, id),
    onSuccess: () => { toast.success("Verbindung getrennt", { description: msg("Der Assistent hat ab sofort keinen Zugriff mehr.") }); void qc.invalidateQueries({ queryKey: ["mcp-connections", tenantId] }); },
  });
  const d = q.data;

  return (
    <div className="pb-16">
      <PageHeader title={t("KI-Assistent")} icon={<Bot className="size-5" />} description={t("Claude & Co. mit euren Logiful-Daten verbinden – Aufträge, Bestände, Wareneingänge und Handlungsbedarf einfach im Chat erfragen.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-4xl">
        {/* Was ist das? */}
        <Card className="p-5">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><MessageSquareText className="size-4 text-fg-muted" /> {t("So fragt ihr eure Daten ab")}</h2>
          <p className="text-[13px] text-fg-muted mt-1">
            {t("Nach der Einrichtung kennt euer KI-Assistent eure Aufträge, Bestände und Wareneingänge bei Logiful und beantwortet Fragen dazu direkt – live aus Connect, ohne Export. Er kann nur")} <b className="text-fg">{t("lesen")}</b>{t(", nichts ändern.")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {EXAMPLES.map((e) => <span key={e} className="rounded-full border border-border bg-surface-2/50 px-3 py-1 text-[12.5px]">„{e}“</span>)}
          </div>
        </Card>

        {/* Einrichten */}
        <Card className="p-5">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><ListChecks className="size-4 text-fg-muted" /> {t("Einrichten – einmal je Person, dauert 2 Minuten")}</h2>
          {!d ? (
            <Skeleton className="h-24 w-full mt-3" />
          ) : !d.licensed ? (
            <p className="mt-3 text-[13px] rounded-lg bg-surface-2/70 px-3 py-2.5">{t("Das Modul „KI-Assistent“ ist noch nicht freigeschaltet – unter Einstellungen → Module buchen oder bei Logiful melden.")}</p>
          ) : (
            <>
              <p className="mt-1 text-[12.5px] text-fg-muted">{t("Eure persönliche Verbindungsadresse (für alle im Team gleich – angemeldet wird jede Person einzeln):")}</p>
              <div className="mt-2"><CopyBox value={d.url} /></div>

              <div className="mt-4 flex w-fit rounded-lg border border-border p-0.5">
                {([["claude", msg("Claude (Web & Desktop)")], ["code", msg("Claude Code")], ["other", msg("Andere Assistenten")]] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setClient(k)} className={cx("h-8 px-3 rounded-md text-[12.5px] font-medium", client === k ? "bg-ink text-ink-fg" : "text-fg-muted hover:text-fg")}>{l}</button>
                ))}
              </div>

              {client === "claude" && (
                <ol className="mt-4 space-y-3">
                  <Step n={1} title={t("Connector hinzufügen")}>
                    <p>{t("In Claude (claude.ai oder Desktop-App) links unten auf euren Namen →")} <b className="text-fg">{t("Einstellungen")}</b> → <b className="text-fg">{t("Connectors")}</b> → <b className="text-fg">{t("Eigenen Connector hinzufügen")}</b>.</p>
                    <p>{t("Im Team- oder Enterprise-Tarif legt das einmal ein Claude-Administrator unter")} <b className="text-fg">{t("Organisationseinstellungen → Connectors")}</b> {t("für alle an; danach verbindet sich jede Person selbst (Schritt 3).")}</p>
                  </Step>
                  <Step n={2} title={t("Name und Adresse eintragen")}>
                    <p>{t("Name:")} <b className="text-fg">{t("Logiful")}</b> {t("· Adresse: die Verbindungsadresse oben. „Erweiterte Einstellungen“ (Client-ID/Secret) leer lassen →")} <b className="text-fg">{t("Hinzufügen")}</b>.</p>
                  </Step>
                  <Step n={3} title={t("Verbinden und zustimmen")}>
                    <p>{t("Beim Connector „Logiful“ auf")} <b className="text-fg">{t("Verbinden")}</b> {t("klicken. Es öffnet sich Logiful Connect: anmelden (wie gewohnt per E-Mail-Link), Kunden wählen,")} <b className="text-fg">{t("Zulassen")}</b>{t(". Danach geht es automatisch zurück zu Claude.")}</p>
                  </Step>
                  <Step n={4} title={t("Im Chat einschalten und fragen")}>
                    <p>{t("In einem neuen Chat unten über das Werkzeug-Symbol prüfen, dass „Logiful“ eingeschaltet ist – dann einfach fragen, z. B. „Welche Aufträge hängen gerade und warum?“.")}</p>
                  </Step>
                </ol>
              )}
              {client === "code" && (
                <ol className="mt-4 space-y-3">
                  <Step n={1} title={t("Server hinzufügen (im Terminal)")}>
                    <CopyBox value={`claude mcp add --transport http logiful ${d.url}`} />
                  </Step>
                  <Step n={2} title={t("Anmelden")}>
                    <p>{t("Claude Code starten,")} <code className="rounded bg-surface-2 px-1">{t("/mcp")}</code> {t("eingeben, „logiful“ wählen →")} <b className="text-fg">{t("Authenticate")}</b>{t(". Im Browser bei Logiful anmelden, Kunden wählen, zulassen.")}</p>
                  </Step>
                  <Step n={3} title={t("Fragen")}>
                    <p>{t("z. B. „Liste alle ausverkauften Artikel als Tabelle und schlag Bestellmengen vor“.")}</p>
                  </Step>
                </ol>
              )}
              {client === "other" && (
                <ol className="mt-4 space-y-3">
                  <Step n={1} title={t("Voraussetzung")}>
                    <p>{t("Der Assistent muss „Remote-MCP-Server“ mit Anmeldung per OAuth unterstützen (Übertragung „Streamable HTTP“). Das können u. a. Claude, Claude Code, ChatGPT (Connectoren/Developer-Modus), Cursor und VS Code.")}</p>
                  </Step>
                  <Step n={2} title={t("Server-Adresse eintragen")}>
                    <p>{t("Als MCP-Server die Verbindungsadresse oben angeben; Client-ID/Secret leer lassen – der Assistent registriert sich selbst.")}</p>
                  </Step>
                  <Step n={3} title={t("Anmelden und zustimmen")}>
                    <p>{t("Beim ersten Zugriff öffnet sich Logiful Connect zur Anmeldung und Zustimmung.")}</p>
                  </Step>
                </ol>
              )}
            </>
          )}
        </Card>

        {/* Was sieht der Assistent? */}
        {d?.licensed && (
          <Card className="p-5">
            <h2 className="font-semibold text-[14px] flex items-center gap-2"><ShieldCheck className="size-4 text-fg-muted" /> {t("Was der Assistent sehen darf")}</h2>
            <ul className="mt-2 space-y-1.5 text-[13px]">
              <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /> <span><b>{t("Nur lesen")}</b> {t("– er kann nichts anlegen, ändern oder stornieren.")}</span></li>
              <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /> <span><b>{t("Gleiche Rechte wie im Portal")}</b> {t("– jede Person sieht über den Assistenten nur, was sie auch hier sehen darf. Rechteänderungen wirken sofort.")}</span></li>
              <li className="flex gap-2">{d.endCustomerData ? <Eye className="size-4 text-warning shrink-0 mt-0.5" /> : <EyeOff className="size-4 text-success shrink-0 mt-0.5" />} <span>{d.endCustomerData ? <><b>{t("Mit Endkundendaten")}</b> {t("– Namen, Adressen, E-Mails eurer Endkunden sind sichtbar.")}</> : <><b>{t("Ohne Endkundendaten")}</b> {t("– von Empfängern nur PLZ, Ort und Land, keine Namen, Straßen oder E-Mails.")}</>}</span></li>
            </ul>
            <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-fg-muted">{t("Verfügbare Abfragen")}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">{d.tools.map((t) => <Badge key={t}>{t}</Badge>)}</div>
            <p className="mt-3 text-[12px] text-fg-subtle">
              {t("Datenschutz: Die abgefragten Daten gehen an den Anbieter eures KI-Assistenten (bei Claude: Anthropic) und werden dort nach dessen Bedingungen verarbeitet. Bitte nur nutzen, wenn das mit euren Datenschutz-Vorgaben vereinbar ist.")}
            </p>
          </Card>
        )}

        {/* Verbindungen */}
        {d?.licensed && (
          <Card className="p-5">
            <h2 className="font-semibold text-[14px] flex items-center gap-2"><Bot className="size-4 text-fg-muted" /> {d.manager ? t("Verbundene Assistenten im Team") : t("Deine verbundenen Assistenten")}</h2>
            {d.connections.length ? (
              <ul className="mt-2 divide-y divide-border">
                {d.connections.map((c) => (
                  <li key={c.id} className="py-2 flex items-center gap-3 text-[13px]">
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{c.client}</span> <span className="text-fg-muted">· {c.name ?? c.email}</span>
                      <span className="block text-[12px] text-fg-subtle">{t("verbunden")} {fmtRelative(c.createdAt)}{c.lastUsedAt ? t(" · zuletzt genutzt {v}", { v: fmtRelative(c.lastUsedAt) }) : t(" · noch nicht genutzt")}</span>
                    </span>
                    <Button size="sm" variant="ghost" icon={<Unplug className="size-3.5" />} loading={revoke.isPending && revoke.variables === c.id} onClick={() => revoke.mutate(c.id)}>{t("Trennen")}</Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-[12.5px] text-fg-subtle">{t("Noch kein Assistent verbunden.")}</p>
            )}
          </Card>
        )}

        {/* Hilfe */}
        <Card className="p-5">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><LifeBuoy className="size-4 text-fg-muted" /> {t("Wenn etwas nicht klappt")}</h2>
          <dl className="mt-2 space-y-2 text-[13px]">
            <div><dt className="font-medium">{t("„Nicht freigeschaltet“ bei der Zustimmung")}</dt><dd className="text-fg-muted text-[12.5px]">{t("Das Modul „KI-Assistent“ ist für diesen Kunden nicht gebucht – unter Einstellungen → Module oder bei Logiful.")}</dd></div>
            <div><dt className="font-medium">{t("Assistent findet nichts / sagt „kein Zugriff“")}</dt><dd className="text-fg-muted text-[12.5px]">{t("Prüfen, ob der Connector im Chat eingeschaltet ist. Fehlt eine Abfrage (z. B. Bestände), hat deine Person im Portal dafür keine Rechte – ein Administrator kann sie unter Einstellungen → Team anpassen.")}</dd></div>
            <div><dt className="font-medium">{t("„Verbindung abgelaufen“ oder getrennt")}</dt><dd className="text-fg-muted text-[12.5px]">{t("Im Assistenten beim Connector „Logiful“ erneut auf „Verbinden“ klicken. Nach 90 Tagen ohne Nutzung ist das ohnehin nötig.")}</dd></div>
            <div><dt className="font-medium">{t("Zeiten wirken verschoben")}</dt><dd className="text-fg-muted text-[12.5px]">{t("Connect liefert Zeiten in UTC; der Assistent rechnet normalerweise in deutsche Zeit um – sonst kurz darum bitten.")}</dd></div>
          </dl>
          <p className="mt-3 text-[12px] text-fg-subtle flex items-center gap-1.5"><Terminal className="size-3.5" /> {t("Für eigene Programme und Automatisierungen gibt es zusätzlich die REST-API unter Einstellungen → Schnittstelle.")}</p>
        </Card>
      </div>
    </div>
  );
}
